/**
 * API Key Manager
 * 统一管理所有 API key，优先级：用户配置（settingsStore）> 环境变量（env）
 */

import { useSettingsStore } from '@/stores/settingsStore';

/**
 * 获取 API key
 * 优先级顺序：
 * 1. 用户配置（settingsStore 中的 apiKeys）- 最高优先级
 * 2. 环境变量（VITE_*_API_KEY）- 向后兼容，较低优先级
 * 
 * @param provider API 提供商名称
 * @returns API key 字符串，如果不存在则返回 null
 */
export function getApiKey(provider: 'twelvedata' | 'coinmarketcap' | 'alphavantage' | 'finnhub' | 'marketstack' | 'financialmodelingprep'): string | null {
  // 优先级 1: 优先从 settingsStore（用户配置）读取
  const apiKeys = useSettingsStore.getState().apiKeys;
  const keyFromStore = apiKeys[provider];
  
  if (keyFromStore && keyFromStore.trim() !== '') {
    return keyFromStore;
  }
  
  // 优先级 2: 如果没有用户配置，尝试从环境变量读取（向后兼容）
  // 注意：环境变量为空字符串时也返回 null，避免使用空值
  const envKeyMap: Record<string, string | null> = {
    twelvedata: (import.meta as any)?.env?.VITE_TWELVEDATA_API_KEY || null,
    coinmarketcap: (import.meta as any)?.env?.VITE_COINMARKETCAP_API_KEY || null,
    alphavantage: (import.meta as any)?.env?.VITE_ALPHAVANTAGE_API_KEY || null,
    finnhub: (import.meta as any)?.env?.VITE_FINNHUB_API_KEY || null,
    marketstack: (import.meta as any)?.env?.VITE_MARKETSTACK_API_KEY || null,
    financialmodelingprep: (import.meta as any)?.env?.VITE_FINANCIALMODELINGPREP_API_KEY || null,
  };
  
  const envKey = envKeyMap[provider];
  // 如果环境变量是空字符串，也返回 null
  return envKey && envKey.trim() !== '' ? envKey : null;
}

/**
 * 设置 API key 到 settingsStore
 */
export function setApiKey(provider: 'twelvedata' | 'coinmarketcap' | 'alphavantage' | 'finnhub' | 'marketstack' | 'financialmodelingprep', key: string | null): void {
  useSettingsStore.getState().setApiKey(provider, key);
}
